import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaSync, FaWifi, FaTimesCircle, FaRubleSign, FaClock, FaUser, FaCashRegister, FaShoppingCart } from 'react-icons/fa';

// Лёгкий API-клиент под кассовые эндпоинты (как в CashShifts)
const cashierAPI = {
  get: async (url) => {
    const token = localStorage.getItem('access_token');
    if (!token) throw new Error('Нет токена');
    const response = await fetch(url, {
      headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    return { data };
  }
};

const formatCurrency = (amount) => {
  const n = Number(amount || 0);
  return `${Math.round(n).toLocaleString('ru-RU')} ₽`;
};

const CurrentCash = () => {
  const [online, setOnline] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openShifts, setOpenShifts] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const timerRef = useRef(null);
  const wsRef = useRef(null);

  const fetchOpenShifts = useCallback(async () => {
    // Берём все открытые смены
    const resp = await cashierAPI.get('/api/cashier/admin/shifts/?status=opened');
    const shifts = (resp.data?.shifts) || [];
    if (shifts.length === 0) {
      setOpenShifts([]);
      return;
    }
    // Подтянем детали для каждой смены (включая транзакции и финансы)
    const detailsPromises = shifts.map(shift => 
      cashierAPI.get(`/api/cashier/admin/shifts/${shift.id}/`)
    );
    const detailsResults = await Promise.all(detailsPromises);
    setOpenShifts(detailsResults.map(r => r.data));
  }, []);

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      await fetchOpenShifts();
      setOnline(true);
      setLastUpdated(new Date());
    } catch (e) {
      setError(e.message);
      setOnline(false);
    } finally {
      setLoading(false);
    }
  }, [fetchOpenShifts]);

  useEffect(() => {
    loadAll();
    timerRef.current = setInterval(loadAll, 5000); // 5 сек
    return () => clearInterval(timerRef.current);
  }, [loadAll]);

  // WebSocket live обновление (мягкий фолбэк на polling)
  useEffect(() => {
    // Включаем WS только если явно задано в окружении
    const WS_ENABLED = process.env.REACT_APP_ENABLE_WS === 'true';
    if (!WS_ENABLED) return;
    const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const urls = [
      `${scheme}://${window.location.host}/ws/admin/current-cash/`,
      `${scheme}://${window.location.host}/ws/cashier/current/`
    ];
    let index = 0;
    let attempts = 0;
    const MAX_ATTEMPTS = 2; // не спамим ошибки
    function connect() {
      if (attempts >= MAX_ATTEMPTS) return;
      attempts += 1;
      try {
        const url = urls[index] || urls[0];
        wsRef.current = new WebSocket(url);
        wsRef.current.onopen = () => setOnline(true);
        wsRef.current.onclose = () => {
          setOnline(false);
          // один раз попробуем второй url, дальше polling
          index = (index + 1) % urls.length;
          setTimeout(connect, 3000);
        };
        wsRef.current.onerror = () => {
          try { wsRef.current.close(); } catch (_) {}
        };
        wsRef.current.onmessage = (evt) => {
          try {
            const msg = JSON.parse(evt.data);
            if (msg.type === 'shift' || msg.type === 'shifts') {
              // Обновляем все смены
              loadAll();
            } else if (msg.type === 'online') {
              setOnline(Boolean(msg.data?.online));
            }
            setLastUpdated(new Date());
          } catch (_) {}
        };
      } catch (_) {}
    }
    connect();
    return () => { try { wsRef.current && wsRef.current.close(); } catch (_) {} };
  }, [loadAll]);

  // Функция для расчета метрик одной смены
  const calculateShiftMetrics = useCallback((shift) => {
    const fin = shift?.financial_data || {};
    const ordersCount = Number(fin.orders_count || 0);
    const totalSales = Number(fin.total_sales || 0);
    const avg = ordersCount > 0 ? Math.round(totalSales / ordersCount) : 0;

    // duration
    const opened = shift?.opened_at ? new Date(shift.opened_at) : null;
    const now = new Date();
    let duration = '-';
    if (opened) {
      const ms = now - opened;
      const h = Math.floor(ms / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      duration = `${h}ч ${m}м`;
    }

    // доля картой
    const card = Number(fin.total_card_sales || 0);
    const share = totalSales > 0 ? Math.round((card / totalSales) * 100) : 0;

    // расчёт текущего наличного баланса
    const list = Array.isArray(shift?.transactions) ? shift.transactions : [];
    const sums = list.reduce((acc, t) => {
      const amount = Number(t.amount || 0);
      if (t.transaction_type === 'cash_in') acc.cash_in += amount;
      if (t.transaction_type === 'cash_out') acc.cash_out += amount;
      if (t.transaction_type === 'refund') acc.refund += amount;
      return acc;
    }, { cash_in: 0, cash_out: 0, refund: 0 });
    const opening = Number(fin.opening_cash || 0);
    const cashSales = Number(fin.total_cash_sales || 0);
    const expectedCash = opening + cashSales + sums.cash_in - sums.cash_out - sums.refund;

    return { avg, duration, cardShare: share, expectedCash };
  }, []);

  // Суммарная статистика по всем открытым сменам
  const totalStats = useMemo(() => {
    if (openShifts.length === 0) return null;
    
    const totals = openShifts.reduce((acc, shift) => {
      const fin = shift?.financial_data || {};
      acc.total_sales += Number(fin.total_sales || 0);
      acc.total_cash += Number(fin.total_cash_sales || 0);
      acc.total_card += Number(fin.total_card_sales || 0);
      acc.orders_count += Number(fin.orders_count || 0);
      return acc;
    }, { total_sales: 0, total_cash: 0, total_card: 0, orders_count: 0 });

    return [
      { label: 'Выручка', value: formatCurrency(totals.total_sales), icon: <FaRubleSign className="w-6 h-6 text-green-600" /> },
      { label: 'Наличные', value: formatCurrency(totals.total_cash), icon: <FaCashRegister className="w-6 h-6 text-blue-600" /> },
      { label: 'Карта', value: formatCurrency(totals.total_card), icon: <FaRubleSign className="w-6 h-6 text-indigo-600" /> },
      { label: 'Заказы', value: String(totals.orders_count), icon: <FaShoppingCart className="w-6 h-6 text-purple-600" /> },
    ];
  }, [openShifts]);

  return (
    <div className="space-y-6">
      {/* Заголовок/статус */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {online ? <FaWifi className="w-5 h-5 text-green-600" /> : <FaTimesCircle className="w-5 h-5 text-red-600" />}
          <span className={`text-sm ${online ? 'text-green-700' : 'text-red-700'}`}>{online ? 'Онлайн' : 'Оффлайн'}</span>
          {lastUpdated && (
            <span className="text-xs text-gray-500">Обновлено: {lastUpdated.toLocaleTimeString('ru-RU')}</span>
          )}
        </div>
        <button onClick={loadAll} className="btn btn-outline flex items-center gap-2" disabled={loading}>
          <FaSync className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Обновить
        </button>
      </div>

      {/* Открытые кассы / заглушка */}
      {openShifts.length === 0 ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800">
          Нет открытых касс.
        </div>
      ) : (
        <>
          {/* Общая статистика по всем кассам */}
          {totalStats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {totalStats.map((item) => (
                <div key={item.label} className="bg-white p-6 rounded-lg shadow-sm border flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">{item.label}</p>
                    <p className="text-2xl font-bold text-gray-900">{item.value}</p>
                  </div>
                  {item.icon}
                </div>
              ))}
            </div>
          )}

          {/* Список открытых касс */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Открытые кассы ({openShifts.length})</h2>
            
            {openShifts.map((shift) => {
              const fin = shift?.financial_data || {};
              const metrics = calculateShiftMetrics(shift);
              const list = Array.isArray(shift?.transactions) ? shift.transactions : [];
              const txBreakdown = list.reduce((acc, t) => {
                const key = t.transaction_type || 'unknown';
                acc[key] = (acc[key] || 0) + 1;
                return acc;
              }, {});

              return (
                <div key={shift.id} className="bg-white rounded-lg shadow-sm border p-6">
                  {/* Заголовок кассы */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div className="flex items-center gap-3">
                      <FaClock className="w-5 h-5 text-blue-600" />
                      <div>
                        <div className="text-sm text-gray-600">Начало смены</div>
                        <div className="font-semibold">{new Date(shift.opened_at).toLocaleString('ru-RU')}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <FaUser className="w-5 h-5 text-gray-600" />
                      <div>
                        <div className="text-sm text-gray-600">Кассир</div>
                        <div className="font-semibold text-lg">{shift.cashier?.name || '—'}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <FaCashRegister className="w-5 h-5 text-indigo-600" />
                      <div>
                        <div className="text-sm text-gray-600">Нал в кассе (расчёт)</div>
                        <div className="font-semibold">{formatCurrency(metrics.expectedCash)}</div>
                      </div>
                    </div>
                  </div>

                  {/* Метрики кассы */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="text-xs text-gray-600">Выручка</div>
                      <div className="text-lg font-semibold text-green-700">{formatCurrency(fin.total_sales)}</div>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="text-xs text-gray-600">Наличные</div>
                      <div className="text-lg font-semibold text-blue-700">{formatCurrency(fin.total_cash_sales)}</div>
                    </div>
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                      <div className="text-xs text-gray-600">Карта</div>
                      <div className="text-lg font-semibold text-indigo-700">{formatCurrency(fin.total_card_sales)}</div>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <div className="text-xs text-gray-600">Заказов</div>
                      <div className="text-lg font-semibold text-purple-700">{fin.orders_count || 0}</div>
                    </div>
                  </div>

                  {/* Дополнительно */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gray-50 border rounded-lg p-3">
                      <div className="text-xs text-gray-600">Средний чек</div>
                      <div className="text-base font-semibold">{formatCurrency(metrics.avg)}</div>
                    </div>
                    <div className="bg-gray-50 border rounded-lg p-3">
                      <div className="text-xs text-gray-600">Длительность</div>
                      <div className="text-base font-semibold">{metrics.duration}</div>
                    </div>
                    <div className="bg-gray-50 border rounded-lg p-3">
                      <div className="text-xs text-gray-600">Продажи: {txBreakdown.sale || 0} / Возвраты: {txBreakdown.refund || 0}</div>
                      <div className="text-xs text-gray-600">Внесения: {txBreakdown.cash_in || 0} / Изъятия: {txBreakdown.cash_out || 0}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">{error}</div>
      )}
    </div>
  );
};

export default CurrentCash;


